import { useEffect, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import JSZip from 'jszip';
import { motion } from 'framer-motion';
import { BsFileImage } from 'react-icons/bs';
import { FiCheck, FiDownload, FiUploadCloud } from 'react-icons/fi';
import SEO from '../components/SEO';
import { imageCompressorSchemas } from '../schemas';
import './ImageCompressor.css';

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_BULK_FILES = 10;

const ImageCompressor = () => {
  const fileInputRef = useRef(null);
  const bulkInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [isBulk, setIsBulk] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('size');
  const [percentage, setPercentage] = useState(50);
  const [targetSize, setTargetSize] = useState(100);
  const [unit, setUnit] = useState('KB');

  useEffect(() => () => images.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl)), [images]);

  const chooseFiles = (fileList, bulk = false) => {
    const selectedFiles = Array.from(fileList || []);
    if (!selectedFiles.length) return;
    if (bulk && selectedFiles.length > MAX_BULK_FILES) {
      setError(`You can upload up to ${MAX_BULK_FILES} images at a time.`);
      return;
    }
    const invalidFile = selectedFiles.find((selectedFile) => !selectedFile.type.startsWith('image/') || selectedFile.size > MAX_FILE_SIZE);
    if (invalidFile) {
      setError('Choose image files no larger than 20 MB each.');
      return;
    }
    setError(null);
    setImages(selectedFiles.map((file) => ({ file, previewUrl: URL.createObjectURL(file), compressedFile: null })));
    setIsBulk(bulk || selectedFiles.length > 1);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    chooseFiles(event.dataTransfer.files);
  };

  const compress = async () => {
    if (!images.length) return;
    const sizeLimit = Number(targetSize) / (unit === 'MB' ? 1 : 1024);
    if (mode === 'size' && (!Number.isFinite(sizeLimit) || sizeLimit <= 0)) {
      setError('Enter a valid compression target.');
      return;
    }
    setIsCompressing(true);
    setError(null);
    
    const startTime = Date.now();
    
    try {
      const processedImages = await Promise.all(images.map(async (image) => {
        const maxSizeMB = mode === 'size' ? sizeLimit : Math.max((image.file.size * Number(percentage)) / 100 / 1024 / 1024, 0.01);
        const compressedFile = await imageCompression(image.file, { maxSizeMB, useWebWorker: false });
        return { ...image, compressedFile };
      }));
      
      // Ensure minimum 3 seconds of animation
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 3000 - elapsed);
      
      setTimeout(() => {
        setImages(processedImages);
        setIsCompressing(false);
      }, remainingTime);
    } catch (compressionError) {
      setError(compressionError.message || 'Could not compress these images. Please try again.');
      setIsCompressing(false);
    }
  };

  const downloadFile = (compressedFile) => {
    if (!compressedFile) return;
    const url = URL.createObjectURL(compressedFile);
    const link = document.createElement('a');
    link.href = url;
    link.download = compressedFile.name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadZip = async () => {
    const zip = new JSZip();
    images.forEach(({ compressedFile }, index) => zip.file(compressedFile.name || `compressed-image-${index + 1}`, compressedFile));
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    downloadFile(new File([zipBlob], 'compressed-images.zip', { type: 'application/zip' }));
  };

  const reset = () => {
    images.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
    setImages([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (bulkInputRef.current) bulkInputRef.current.value = '';
  };

  const allCompressed = images.length > 0 && images.every(({ compressedFile }) => compressedFile);

  const PreviewCard = ({ image, index }) => {
    const small = isBulk;
    const newSize = image.compressedFile ? (image.compressedFile.size / 1024).toFixed(1) : 0;
    return <div className={small ? 'image-preview-card-small' : 'image-preview-card'}>
      {isCompressing ? <div className={small ? 'compressing-state-small' : 'compressing-state'}><motion.div className={small ? 'spinner-small' : 'spinner'} animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} />{!small && <p className="compressing-text">Compressing your image…</p>}</div> : <div className={small ? 'preview-image-wrapper-small' : 'preview-image-wrapper'}><img className={small ? 'preview-image-small' : 'preview-image'} src={image.previewUrl} alt={image.file.name} />{image.compressedFile && <span className={small ? 'success-badge-small' : 'success-badge'}><FiCheck /></span>}</div>}
      <div className={small ? 'preview-info-small' : 'preview-info'}><p className={small ? 'preview-title-small' : 'preview-title'}>{image.file.name}</p><p className={small ? 'preview-format-small' : 'preview-format'}>Original: {(image.file.size / 1024).toFixed(1)} KB {image.compressedFile && <span className="size-reduction">• New size: {newSize} KB</span>}</p></div>
      {image.compressedFile && <button className="download-single-btn" onClick={() => downloadFile(image.compressedFile)}><FiDownload /> Download</button>}
    </div>;
  };

  return <div className="compressor-page">
    <SEO 
      title="Image Compressor – Reduce Image Size Online Free"
      description="Compress JPG, PNG & WEBP images online without losing quality. Reduce image file size instantly for web, email or upload — 100% free."
      keywords="image compressor, reduce image size, compress jpg online"
      path="/image-compressor"
      schema={imageCompressorSchemas}
    />
    <div className="compressor-container">
    <header className="compressor-header"><h1 className="compressor-title">Reduce Image Size</h1><p className="compressor-subtitle">Reduce image file sizes without quality loss.</p></header>
    
    <div className="compressor-content-wrapper">
      {/* Left Decorative Image */}
      <img 
        src="/img-6.png" 
        alt="Decorative" 
        className="compressor-decorative-img compressor-decorative-img-left"
      />

      {/* Right Decorative Image */}
      <img 
        src="/img-5.png" 
        alt="Decorative" 
        className="compressor-decorative-img compressor-decorative-img-right"
      />

      <div className="compressor-main-content">
        {error && <div className="error-message"><div className="error-content"><span className="error-icon">!</span><p className="error-text">{error}</p><button className="error-close" onClick={() => setError(null)} aria-label="Dismiss error">×</button></div></div>}
        {!images.length ? <motion.div className={`upload-area ${isDragging ? 'dragging' : ''}`} onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }} onDragLeave={() => setIsDragging(false)} onDrop={handleDrop} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}><div className="upload-content"><div className="upload-icon-wrapper"><FiUploadCloud className="upload-icon" /></div><h2 className="upload-title">Upload an image</h2><p className="upload-description">Drag and drop an image here, or choose up to 10 images for bulk compression.</p><div className="upload-buttons-row"><button type="button" className="upload-button" onClick={() => fileInputRef.current?.click()}><BsFileImage /> Choose image</button><button type="button" className="upload-button bulk" onClick={() => bulkInputRef.current?.click()}><BsFileImage /> Bulk upload</button></div><input ref={fileInputRef} type="file" accept="image/*" onChange={(event) => chooseFiles(event.target.files)} hidden /><input ref={bulkInputRef} type="file" accept="image/*" multiple onChange={(event) => chooseFiles(event.target.files, true)} hidden /></div><div className="upload-glow" /></motion.div> : <div className="compress-container">
          <div className={`images-grid ${isBulk ? 'bulk' : 'single'}`}>{images.map((image, index) => <PreviewCard image={image} index={index} key={`${image.file.name}-${index}`} />)}</div>
          {!allCompressed && <div className="compression-controls"><div className="tabs"><button className={`tab ${mode === 'size' ? 'active' : ''}`} onClick={() => setMode('size')}>Target file size</button><button className={`tab ${mode === 'percentage' ? 'active' : ''}`} onClick={() => setMode('percentage')}>Reduce by percentage</button></div><div className="tab-content">{mode === 'size' ? <div className="size-input-group"><label className="input-label" htmlFor="target-size">Maximum output size</label><div className="input-with-unit"><input id="target-size" className="size-input" type="number" min="1" value={targetSize} onChange={(event) => setTargetSize(event.target.value)} /><select className="unit-select" value={unit} onChange={(event) => setUnit(event.target.value)}><option>KB</option><option>MB</option></select></div></div> : <div className="percentage-input-group"><label className="input-label" htmlFor="percentage">Target size</label><div className="percentage-input-wrapper"><input id="percentage" className="percentage-input" type="number" min="1" max="100" value={percentage} onChange={(event) => setPercentage(event.target.value)} /><span className="percentage-symbol">%</span></div></div>}</div><button className="compress-button" onClick={compress} disabled={isCompressing}>{isCompressing ? 'Compressing…' : 'Compress image'}</button></div>}
          <div className="action-buttons">{isBulk && allCompressed && <button className="download-button" onClick={downloadZip}><FiDownload /> Download bulk ZIP</button>}{!isBulk && images[0]?.compressedFile && <button className="download-button" onClick={() => downloadFile(images[0].compressedFile)}><FiDownload /> Download compressed image</button>}<button className="reset-button" onClick={reset}>Choose another image</button></div>
        </div>}
      </div>
    </div>
  </div></div>;
};

export default ImageCompressor;
